"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MediaStreamController = exports.optionsKey = exports.mediaController = exports.CONSTRAINTS_SCREENCAPTURE_OPTIMAL_WEAK = exports.CONSTRAINTS_VIDEO_OPTIMAL_WEAK = exports.CONSTRAINTS_VIDEO_OPTIMAL_STRICT = exports.CONSTRAINTS_AUDIO_OPTIMAL_WEAK = exports.CONSTRAINTS_AUDIO_OPTIMAL_STRICT = exports.VIDEO_DEVICE_CAPTURABLE = exports.VIDEO_DEVICE_SCREENCAPTURE = void 0;
const tslib_1 = require("tslib");
/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
const log_client_1 = require("@video/log-client");
const source_1 = tslib_1.__importDefault(require("@video/log-client/lib/source"));
const deep_object_diff_1 = require("deep-object-diff");
const mobx_1 = require("mobx");
const api_1 = require("../api");
const adapter_1 = require("../api/adapter");
const dom_exception_1 = require("../api/typings/dom-exception");
const package_json_1 = tslib_1.__importDefault(require("../package-json"));
const common_1 = require("../utils/common");
const errors_1 = require("./errors");
const bind_1 = require("./utils/bind");
const event_emitter_1 = require("./utils/events/event-emitter");
const user_agent_1 = require("./adapters/utils/user-agent");
//region Default Constraints
exports.VIDEO_DEVICE_SCREENCAPTURE = {
    deviceId: "screencapture",
    groupId: "screencapture",
    label: "Screen Capture",
    kind: "videoinput",
    virtual: true,
    // it's any in original DOM type
    // eslint-disable-next-line
    toJSON() {
        return {
            deviceId: this.deviceId,
            groupId: this.groupId,
            label: this.label,
            kind: this.kind,
        };
    },
};
exports.VIDEO_DEVICE_CAPTURABLE = {
    deviceId: "capturable",
    groupId: "capturable",
    label: "Capturable",
    kind: "videoinput",
    virtual: true,
    // it's any in original DOM type
    // eslint-disable-next-line
    toJSON() {
        return {
            deviceId: this.deviceId,
            groupId: this.groupId,
            label: this.label,
            kind: this.kind,
        };
    },
};
function isVirtualDevice(dev) {
    return dev != null && dev.virtual;
}
/**
 * Whether a device API is running on Android via react-native
 * @param deviceApi Device
 * @returns True if running on Android via react-native, false otherwise
 */
function isReactNativeAndroidDevice(deviceApi) {
    return deviceApi.isAndroidDevice && deviceApi.platform === "ReactNative";
}
exports.CONSTRAINTS_AUDIO_OPTIMAL_STRICT = {
    autoGainControl: { exact: true },
    channelCount: { min: 1, ideal: 2 },
    echoCancellation: { exact: true },
    latency: { ideal: 0 },
    noiseSuppression: { exact: true },
    sampleRate: { min: 8000, ideal: 48000, max: 48000 },
    sampleSize: { min: 16, ideal: 32 },
};
exports.CONSTRAINTS_AUDIO_OPTIMAL_WEAK = {
    autoGainControl: { ideal: true },
    channelCount: { min: 1, ideal: 2 },
    echoCancellation: { ideal: true },
    latency: { ideal: 0 },
    noiseSuppression: { ideal: true },
    sampleRate: { min: 8000, ideal: 48000, max: 48000 },
    sampleSize: { min: 16, ideal: 32 },
};
exports.CONSTRAINTS_VIDEO_OPTIMAL_STRICT = {
    facingMode: { ideal: "user" },
    aspectRatio: { exact: 16 / 9 },
    height: adapter_1.device.isAndroidDevice
        ? { min: 240, max: 720 }
        : { min: 240, ideal: adapter_1.device.isIosDevice ? 720 : 1080, max: adapter_1.device.isIosDevice ? 720 : 1080 },
    frameRate: { ideal: 24 },
};
exports.CONSTRAINTS_VIDEO_OPTIMAL_WEAK = {
    facingMode: { ideal: "user" },
    aspectRatio: { ideal: 16 / 9 },
    height: adapter_1.device.isAndroidDevice
        ? { min: 240, max: 720 }
        : { min: 240, ideal: adapter_1.device.isIosDevice ? 720 : 1080, max: adapter_1.device.isIosDevice ? 720 : 1080 },
    frameRate: { ideal: 24 },
};
exports.CONSTRAINTS_SCREENCAPTURE_OPTIMAL_WEAK = {
    frameRate: { ideal: 24 },
};
//endregion
//region Static Properties
const CURRENT_DEVICE_RESOLUTION_VERSION = "1.0";
const MOBX_REACT_DELAY = 10;
const mediaDevicesList = mobx_1.observable.array([], { name: "mediaDevicesList" });
const videoDevices = new Proxy({}, {
    get: (target, prop) => {
        var _a;
        if (prop === exports.VIDEO_DEVICE_SCREENCAPTURE.deviceId) {
            return exports.VIDEO_DEVICE_SCREENCAPTURE;
        }
        if (prop === exports.VIDEO_DEVICE_CAPTURABLE.deviceId) {
            return exports.VIDEO_DEVICE_CAPTURABLE;
        }
        return (_a = mediaDevicesList.find((d) => d.deviceId === prop && d.kind === "videoinput")) !== null && _a !== void 0 ? _a : null;
    },
});
const AUDIO_GAIN_MULTIPLIER = 10;
const audioDevices = new Proxy({}, {
    get: (target, prop) => {
        var _a;
        return (_a = mediaDevicesList.find((d) => d.deviceId === prop && d.kind === "audioinput")) !== null && _a !== void 0 ? _a : null;
    },
});
const allDevices = new Proxy({}, {
    get: (target, prop) => {
        var _a;
        if (prop === exports.VIDEO_DEVICE_SCREENCAPTURE.deviceId) {
            return exports.VIDEO_DEVICE_SCREENCAPTURE;
        }
        if (prop === exports.VIDEO_DEVICE_CAPTURABLE.deviceId) {
            return exports.VIDEO_DEVICE_CAPTURABLE;
        }
        return (_a = mediaDevicesList.find((d) => d.deviceId === prop)) !== null && _a !== void 0 ? _a : null;
    },
});
const possibleResolutions = [4320, 2160, 1440, 1080, 720, 480, 360, 240, 180];
const tracksMeta = new WeakMap();
const deviceResolutionsCache = {};
let deviceUpdateTimer = 0;
let audioCtx = null;
const defaultMediaStreamControllerOptions = {
    defaultConstraints: {
        audio: exports.CONSTRAINTS_AUDIO_OPTIMAL_STRICT,
        video: exports.CONSTRAINTS_VIDEO_OPTIMAL_STRICT,
        screencapture: exports.CONSTRAINTS_SCREENCAPTURE_OPTIMAL_WEAK,
    },
    fallbackConstraints: {
        audio: exports.CONSTRAINTS_AUDIO_OPTIMAL_WEAK,
        video: exports.CONSTRAINTS_VIDEO_OPTIMAL_WEAK,
        screencapture: {},
    },
    replaceTracks: true,
    waitingDelay: 100,
    // probably can be ExistsStreamPolicy.ignore for chrome
    defaultLockPolicy: api_1.ExistsStreamPolicy.wait,
    noEchoGainAmplifier: adapter_1.device.isIosDevice,
    requestAudioPermission: true,
};
//endregion
//region Helper Functions
function attachGainController(logger, stream, gain) {
    if (!adapter_1.device.isImplements(adapter_1.Feature.AUDIO_CONTEXT)) {
        return stream;
    }
    if (!stream.getAudioTracks().length) {
        // no audio tracks
        return stream;
    }
    logger.debug("attach gain controller to stream");
    // // check unsupported browsers
    if (audioCtx == null) {
        audioCtx = new adapter_1.device.AudioContext();
    }
    // check unsupported browsers
    if (audioCtx.createAnalyser == null ||
        audioCtx.createGain == null ||
        audioCtx.createMediaStreamSource == null ||
        audioCtx.createMediaStreamDestination == null) {
        return stream;
    }
    // will be reimplemented in separate class
    // // create reusable analyzer elements
    // if (this.analyzer == null) {
    //   this.analyzer = this.audioCtx.createAnalyser();
    //   this.analyzer.fftSize = 256;
    //   this.analyzerData = new Uint8Array(this.analyzer.frequencyBinCount);
    // }
    // connect source and destination
    const gainNode = audioCtx.createGain();
    const src = audioCtx.createMediaStreamSource(stream);
    const gainDestination = audioCtx.createMediaStreamDestination();
    const gainStream = gainDestination.stream;
    src.connect(gainNode);
    gainNode.connect(gainDestination);
    // gainNode.connect(this.analyzer);
    gainNode.gain.value = gain;
    // original audio tracks
    const originalTrack = stream.getAudioTracks()[0];
    const gainTrack = gainStream.getAudioTracks()[0];
    if (originalTrack == null || gainTrack == null) {
        throw new Error("no audio tracks");
    }
    // set the original device id for gainstream's new audio tracks
    const deviceId = findDeviceId(originalTrack);
    if (deviceId != null) {
        tracksMeta.set(gainTrack, { deviceId, originalTrack, gainNode });
    }
    // add video tracks to the new stream
    stream.getVideoTracks().forEach((t) => {
        gainStream.addTrack(t);
    });
    // clean up old tracks
    const onEnded = () => {
        logger.debug("modified audio track is ended. stopping original one", { originalTrack: originalTrack.id });
        originalTrack.stop();
        gainTrack.removeEventListener("ended", onEnded);
    };
    gainTrack.addEventListener("ended", onEnded);
    return gainStream;
}
function stopTracks({ stream, audio = true, video = true, removeEventListenerCb }) {
    var _a;
    if (stream == null) {
        return;
    }
    for (const track of stream.getTracks()) {
        // skip audio or video tracks
        if ((track.kind === "audio" && !audio) || (track.kind === "video" && !video)) {
            // eslint-disable-next-line no-continue
            continue;
        }
        if (removeEventListenerCb != null) {
            removeEventListenerCb(track);
        }
        const original = tracksMeta.get(track);
        if (original != null) {
            if (removeEventListenerCb != null && original.originalTrack != null) {
                removeEventListenerCb(original.originalTrack);
            }
            (_a = original.originalTrack) === null || _a === void 0 ? void 0 : _a.stop();
        }
        track.stop();
        stream.removeTrack(track);
    }
}
function getSettings(track) {
    var _a;
    let settings;
    try {
        settings = (_a = track.getSettings) === null || _a === void 0 ? void 0 : _a.call(track);
    }
    catch (error) {
        settings = {};
    }
    return settings;
}
function getCapabilities(track, logger) {
    var _a, _b;
    let capabilities;
    try {
        capabilities = (_b = (_a = track === null || track === void 0 ? void 0 : track.getCapabilities) === null || _a === void 0 ? void 0 : _a.call(track)) !== null && _b !== void 0 ? _b : {};
        logger === null || logger === void 0 ? void 0 : logger.debug("videoTrack capabilities", { capabilities });
    }
    catch (error) {
        capabilities = {};
    }
    return capabilities;
}
function getConstraints(track) {
    var _a;
    let constraints;
    try {
        constraints = (_a = track.getConstraints) === null || _a === void 0 ? void 0 : _a.call(track);
    }
    catch (error) {
        constraints = {};
    }
    return constraints;
}
function findDeviceId(track) {
    var _a, _b, _c;
    const meta = tracksMeta.get(track);
    if ((meta === null || meta === void 0 ? void 0 : meta.deviceId) != null) {
        return meta.deviceId;
    }
    const settings = getSettings(track);
    const constraints = getConstraints(track);
    const deviceId = (_a = settings === null || settings === void 0 ? void 0 : settings.deviceId) !== null && _a !== void 0 ? _a : 
    // eslint-disable-next-line no-nested-ternary
    (typeof (constraints === null || constraints === void 0 ? void 0 : constraints.deviceId) === "object"
        ? constraints.deviceId.exact
        : Array.isArray(constraints.deviceId)
            ? constraints.deviceId[0]
            : constraints.deviceId);
    if (deviceId != null) {
        return deviceId.toString();
    }
    return (_c = (_b = mediaDevicesList.find((d) => d.label === track.label)) === null || _b === void 0 ? void 0 : _b.deviceId) !== null && _c !== void 0 ? _c : null;
}
function calculateDeviceResolutions({ stream, logger, deviceInUse = false, }) {
    var _a, _b, _c, _d;
    const vt = stream.getVideoTracks()[0];
    if (vt == null) {
        return [null, null];
    }
    const capabilities = getCapabilities(vt, logger);
    const settings = getSettings(vt);
    const maxWidth = deviceInUse || ((_a = capabilities === null || capabilities === void 0 ? void 0 : capabilities.width) === null || _a === void 0 ? void 0 : _a.max) == null ? settings === null || settings === void 0 ? void 0 : settings.width : (_b = capabilities === null || capabilities === void 0 ? void 0 : capabilities.width) === null || _b === void 0 ? void 0 : _b.max;
    let maxHeight = deviceInUse || ((_c = capabilities === null || capabilities === void 0 ? void 0 : capabilities.height) === null || _c === void 0 ? void 0 : _c.max) == null ? settings === null || settings === void 0 ? void 0 : settings.height : (_d = capabilities === null || capabilities === void 0 ? void 0 : capabilities.height) === null || _d === void 0 ? void 0 : _d.max;
    // Regardless of capabilities, we are only allowing
    // 2160 max for web device and 1080 for mobile devices.
    // don't touch!
    const maxAllowedWidth = adapter_1.device.isMobileDevice ? 1080 : 2160;
    if (maxHeight != null && maxHeight > maxAllowedWidth) {
        maxHeight = maxAllowedWidth;
    }
    if (maxHeight == null || maxWidth == null) {
        return [null, null];
    }
    logger === null || logger === void 0 ? void 0 : logger.debug("adjusted maxHeight", { adjustedMaxHeight: maxHeight });
    return [maxWidth, maxHeight];
}
function mergeConstraints(constraints, deviceId, overrides) {
    const copy = JSON.parse(JSON.stringify(constraints));
    if (overrides.aspectRatio != null) {
        if (typeof overrides.aspectRatio === "number") {
            copy.aspectRatio = { exact: overrides.aspectRatio };
        }
        else {
            const [min, max] = overrides.aspectRatio;
            copy.aspectRatio = { min, max };
        }
    }
    if (overrides.autoGainControl != null) {
        copy.autoGainControl = { exact: overrides.autoGainControl };
    }
    if (overrides.echoCancellation != null) {
        copy.echoCancellation = { exact: overrides.echoCancellation };
    }
    if (overrides.facingMode != null) {
        copy.facingMode = { exact: overrides.facingMode };
    }
    if (overrides.frameRate != null) {
        if (typeof overrides.frameRate === "number") {
            /**
             * For backwards compatibility
             */
            copy.frameRate = { exact: overrides.frameRate };
        }
        else if (Array.isArray(overrides.frameRate)) {
            /**
             * Array check for backwards compatibility
             * @todo remove with next breaking change
             **/
            const [min, max] = overrides.frameRate;
            copy.frameRate = { min, max };
        }
        else {
            copy.frameRate = overrides.frameRate;
        }
    }
    if (overrides.resolution != null) {
        if (typeof overrides.resolution === "number") {
            copy.height = { exact: overrides.resolution };
        }
        else if (Array.isArray(overrides.resolution)) {
            const [min, max] = overrides.resolution;
            copy.height = { min, max };
        }
        else {
            copy.height = { min: overrides.resolution.min, ideal: overrides.resolution.ideal, max: overrides.resolution.max };
        }
    }
    copy.deviceId = deviceId == null || isVirtualDevice(allDevices[deviceId]) ? undefined : { exact: deviceId };
    return copy;
}
function emptyBatchUpdates() {
    return {
        timeout: null,
        changes: [],
        videoDevice: false,
        audioDevice: false,
        gain: false,
        videoPaused: false,
        audioMuted: false,
        constraints: false,
    };
}
function buildStreamConstraints({ defaults, audioDeviceInfo, videoDeviceInfo, overrides, }) {
    const { echoCancellation, autoGainControl, noiseSuppression, frameRate, resolution, aspectRatio, facingMode } = overrides;
    const video = (videoDeviceInfo === null || videoDeviceInfo === void 0 ? void 0 : videoDeviceInfo.deviceId) === (exports.VIDEO_DEVICE_SCREENCAPTURE === null || exports.VIDEO_DEVICE_SCREENCAPTURE === void 0 ? void 0 : exports.VIDEO_DEVICE_SCREENCAPTURE.deviceId) ? defaults.screencapture : defaults.video;
    const { audio } = defaults;
    return {
        audio: audioDeviceInfo == null
            ? false
            : mergeConstraints(audio, audioDeviceInfo === null || audioDeviceInfo === void 0 ? void 0 : audioDeviceInfo.deviceId, {
                echoCancellation,
                autoGainControl,
                noiseSuppression,
            }),
        video: videoDeviceInfo == null
            ? false
            : mergeConstraints(video, videoDeviceInfo === null || videoDeviceInfo === void 0 ? void 0 : videoDeviceInfo.deviceId, {
                resolution,
                frameRate,
                aspectRatio,
                facingMode,
            }),
    };
}
function deviceResolutionCacheKey(deviceId) {
    return `deviceResolution:${CURRENT_DEVICE_RESOLUTION_VERSION}:[${deviceId}]`;
}
//endregion
/**
 * Factory for MediaStreamController and enumerator for media devices.
 * It requires to call init() before start using it
 */
class MediaController extends event_emitter_1.ObservableEventEmitter {
    constructor() {
        var _a, _b;
        super();
        this.isInitialized = false;
        this.initialized = null;
        this._supportSharedDevices = null;
        this.camPermission = null;
        this.micPermission = null;
        this.safariPermissionStatusInterval = undefined;
        this.deviceInUseInterval = undefined;
        this.devicesInUse = [];
        this.logger = new log_client_1.LoggerCore(common_1.PACKAGE_NAME)
            .setLoggerMeta("client", "VDC")
            .setLoggerMeta("release", package_json_1.default.version)
            .appendChain(MediaController)
            .setMessageAggregate("contextId", (_a = (0, common_1.contextId)()) !== null && _a !== void 0 ? _a : undefined)
            .setMessageAggregate("instanceId", (_b = (0, common_1.instanceId)()) !== null && _b !== void 0 ? _b : undefined);
        (0, bind_1.makeBounded)(this);
        (0, mobx_1.makeObservable)(this, {
            requestController: mobx_1.action,
            devicesInUse: mobx_1.observable,
        });
    }
    /**
     * Enumerate all audio devices.
     *
     * @throws NotInitializedError if called before init()
     */
    audioDevices() {
        if (!this.isInitialized) {
            this.logger.warn("init() must be called on the media-controller singleton", {});
            return [];
        }
        return mediaDevicesList.filter((md) => md.kind === "audioinput");
    }
    /**
     * Enumerate all video devices.
     *
     * @emits videoDeviceChanged if the device has changed
     * @throws NotInitializedError if called before init()
     */
    videoDevices() {
        if (!this.isInitialized) {
            this.logger.warn("init() must be called on the media-controller singleton", {});
            return [];
        }
        const mediaDevices = mediaDevicesList.filter((md) => md.kind === "videoinput");
        return mediaDevices;
    }
    get devices() {
        return Object.fromEntries(mediaDevicesList.entries());
    }
    /**
     * If the browser/platform supports sharing video/audio devices amongst
     * different MediaStreams, i.e.:
     *
     * const stream1 = await navigator.mediaDevices.getUserMedia({video: "abc123"});
     * const stream2 = await navigator.mediaDevices.getUserMedia({video: "abc123"});
     *
     */
    get supportSharedDevices() {
        if (this._supportSharedDevices == null) {
            return false;
        }
        return this._supportSharedDevices;
    }
    async checkSupportSharedDevices(hasVideoPermission, hasAudioPermission) {
        var _a, _b, _c, _d;
        if (isReactNativeAndroidDevice(adapter_1.device) || adapter_1.device.isAndroidDevice) {
            // The way we check for shared device support via [testConstraint] will throw an OS camera error most of the time.
            // this is because of too many open camera sessions. remember, this is not the only place where we use [getUserMedia].
            // practically speaking on mobile, how likely is device sharing across streams anyways?
            this._supportSharedDevices = false;
            return;
        }
        if (!adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE)) {
            this._supportSharedDevices = false;
            return;
        }
        if (hasVideoPermission === false && hasAudioPermission === false) {
            this.logger.warn("video and audio device permissions denied");
            this._supportSharedDevices = false;
            return;
        }
        if (adapter_1.device.isImplements(adapter_1.Feature.LOCAL_STORAGE)) {
            const supportSharedDevices = adapter_1.device.localStorage.getItem("supportSharedDevices");
            if (supportSharedDevices != null) {
                this._supportSharedDevices = JSON.parse(supportSharedDevices);
                return;
            }
        }
        let stream1 = null;
        let stream2 = null;
        try {
            const testConstraint = hasVideoPermission
                ? { video: { deviceId: "default" } }
                : { audio: { deviceId: "default" } };
            stream1 = await adapter_1.device.mediaDevices.getUserMedia(testConstraint);
            const deviceId = (_b = (_a = stream1.getTracks()[0]) === null || _a === void 0 ? void 0 : _a.getSettings()) === null || _b === void 0 ? void 0 : _b.deviceId;
            const testConstraint2 = hasVideoPermission ? { video: { deviceId } } : { audio: { deviceId } };
            stream2 = await adapter_1.device.mediaDevices.getUserMedia(testConstraint2);
            const deviceId2 = (_d = (_c = stream2.getTracks()[0]) === null || _c === void 0 ? void 0 : _c.getSettings()) === null || _d === void 0 ? void 0 : _d.deviceId;
            // If the two deviceIds are the same, shared devices is possible, otherwise it's just grabbing a fallback device
            // bc the requested device is already in use/unavailable.
            this._supportSharedDevices = deviceId === deviceId2;
        }
        catch {
            this._supportSharedDevices = false;
        }
        finally {
            stopTracks({ stream: stream1 });
            stopTracks({ stream: stream2 });
            if (adapter_1.device.isImplements(adapter_1.Feature.LOCAL_STORAGE)) {
                adapter_1.device.localStorage.setItem("supportSharedDevices", JSON.stringify(this._supportSharedDevices));
            }
        }
    }
    /**
     * Initialize media controller with default video/audio device
     * Should be called before any other methods
     * Some platforms requires to call this method on user action (like mouse click)
     *
     * @param {MediaControllerOptions} options
     * @returns {Promise<void>}
     */
    async init(options) {
        this.mediaControllerOptions = options;
        if (this.initialized != null) {
            if (options) {
                this.setOptions(options);
            }
            return this.initialized;
        }
        this.initialized = this._init();
        if (options) {
            this.setOptions(options);
        }
        try {
            await this.initialized;
        }
        catch (error) {
            this.initialized = null;
            // todo: logging
            throw error;
        }
        this.isInitialized = true;
        return this.initialized;
    }
    setOptions(options) {
        var _a;
        clearInterval(deviceUpdateTimer);
        if (options.updateByTimer != null) {
            deviceUpdateTimer = adapter_1.device.setInterval(this.enumerateDevices, options.updateByTimer);
        }
        if (options.logger != null) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.destroy();
            this.logger = options.logger;
        }
    }
    handleDeviceChange() {
        this.enumerateDevices(false);
    }
    /**
     * internal logic for locked init
     *
     * @param {MediaControllerOptions} options
     * @returns {Promise<void>}
     */
    async _init() {
        if (!adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE)) {
            this.throwError((0, errors_1.createError)(api_1.ErrorCode.MediaDeviceNotSupported, "media device is not supported", {}));
        }
        adapter_1.device.mediaDevices.removeEventListener("devicechange", this.handleDeviceChange);
        adapter_1.device.mediaDevices.addEventListener("devicechange", this.handleDeviceChange);
        // call it second time because it changes after permission granted
        return this.enumerateDevices(true).then(this.observePerms);
    }
    async observePerms() {
        var _a, _b;
        if (!adapter_1.device.isImplements(adapter_1.Feature.PERMISSIONS)) {
            return;
        }
        if ((0, user_agent_1.hasPermissionsChangeSupport)(adapter_1.device)) {
            try {
                // Cam Permission
                this.camPermission = await adapter_1.device.permissions.query({ name: "camera" });
                this.camPermission.addEventListener("change", this.handlePermissionStatusChange);
                // Mic Permission
                this.micPermission = await adapter_1.device.permissions.query({ name: "microphone" });
                this.micPermission.addEventListener("change", this.handlePermissionStatusChange);
            }
            catch (err) {
                let msg = {};
                if (err instanceof Error) {
                    msg = { errName: err.name, errMsg: err.message };
                }
                this.logger.debug("Unable to query device permissions", msg);
            }
            finally {
                this.addInnerDisposer(() => {
                    var _a, _b;
                    (_a = this.camPermission) === null || _a === void 0 ? void 0 : _a.removeEventListener("change", this.handlePermissionStatusChange);
                    (_b = this.micPermission) === null || _b === void 0 ? void 0 : _b.removeEventListener("change", this.handlePermissionStatusChange);
                });
            }
        }
        else {
            // Safari Polyfill for Safari 16.4
            this.safariPermissionChangePolyfill();
        }
        this.logger.debug("observePerms()", {
            cam: (_a = this.camPermission) === null || _a === void 0 ? void 0 : _a.state,
            mic: (_b = this.micPermission) === null || _b === void 0 ? void 0 : _b.state,
        });
    }
    handlePermissionStatusChange(ev) {
        var _a, _b, _c, _d;
        if (ev.target.name === "camera") {
            this.camPermission = ev.target;
        }
        if (ev.target.name === "microphone") {
            this.micPermission = ev.target;
        }
        const target = ev.target;
        this.enumerateDevices(target.state === "prompt");
        this.emit("permissionsChanged", { name: (_a = ev.target) === null || _a === void 0 ? void 0 : _a.name, state: (_b = ev.target) === null || _b === void 0 ? void 0 : _b.state });
        this.logger.debug("permissionsChanged", { name: (_c = ev.target) === null || _c === void 0 ? void 0 : _c.name, state: (_d = ev.target) === null || _d === void 0 ? void 0 : _d.state });
    }
    safariPermissionChangePolyfill() {
        this.logger.debug("Setting safariPermissionStatusInterval");
        // if Safari 16.3 or less
        adapter_1.device.clearInterval(this.safariPermissionStatusInterval);
        this.safariPermissionStatusInterval = adapter_1.device.setInterval(async () => {
            var _a, _b, _c, _d;
            if (!adapter_1.device.isImplements(adapter_1.Feature.PERMISSIONS)) {
                return;
            }
            // compare current permissions state to stored permissions state
            const permCam = await adapter_1.device.permissions.query({ name: "camera" });
            const permMic = await adapter_1.device.permissions.query({ name: "microphone" });
            // if not changed do nothing
            if (permCam.state === ((_a = this.camPermission) === null || _a === void 0 ? void 0 : _a.state) && permMic.state === ((_b = this.micPermission) === null || _b === void 0 ? void 0 : _b.state)) {
                return;
            }
            if (permCam.state !== ((_c = this.camPermission) === null || _c === void 0 ? void 0 : _c.state)) {
                this.handlePermissionStatusChange({ target: permCam });
            }
            if (permMic.state !== ((_d = this.micPermission) === null || _d === void 0 ? void 0 : _d.state)) {
                this.handlePermissionStatusChange({ target: permMic });
            }
        }, 1000);
        this.addInnerDisposer(() => adapter_1.device.clearInterval(this.safariPermissionStatusInterval));
    }
    /**
     * MediaStreamController factory. Creates MediaStreamController
     * based on giving constraints. Array order is important
     *
     * Important! You have to call ctrl.dispose() when you finish work
     * with MediaStreamController. Otherwise it continue capture
     * audio and video devices.
     *
     * @throws {ErrorCode.MediaControllerNotInitialized} if called before init()
     * @throws {ErrorCode.MediaStreamNotSupported} if MediaStream is not supported by the current device
     **/
    requestController(options = defaultMediaStreamControllerOptions) {
        var _a, _b;
        const combinedOptions = {
            ...defaultMediaStreamControllerOptions,
            replaceTracks: this.supportSharedDevices,
            requestAudioPermission: (_b = (_a = this.mediaControllerOptions) === null || _a === void 0 ? void 0 : _a.requestAudioPermission) !== null && _b !== void 0 ? _b : true,
            ...options,
        };
        this.logger.info("request new MediaStreamController", {
            options: {
                defaultConstraints: JSON.stringify(combinedOptions.defaultConstraints),
                fallbackConstraints: JSON.stringify(combinedOptions.fallbackConstraints),
                defaultLockPolicy: combinedOptions.defaultLockPolicy,
                replaceTracks: combinedOptions.replaceTracks,
                waitingDelay: combinedOptions.waitingDelay,
                displayName: combinedOptions.displayName,
                requestAudioPermission: combinedOptions.requestAudioPermission,
            },
        });
        if (!this.isInitialized) {
            this.throwError((0, errors_1.createError)(api_1.ErrorCode.MediaControllerNotInitialized, "init() must be called on the media-controller singleton", {}));
        }
        return Promise.resolve(new MediaStreamController(combinedOptions));
    }
    async enumerateDevices(shouldRequestPermissions = false) {
        var _a, _b;
        let deviceInUse = { audio: false, video: false };
        if (!adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE) || adapter_1.device.mediaDevices.enumerateDevices == null) {
            this.throwError((0, errors_1.createError)(api_1.ErrorCode.MediaDeviceNotSupported, "media device is not supported", {}));
        }
        // On Firefox, getUserMedia streams need to be active when enumerateDevices() is
        // called or it won't return device labels: https://stackoverflow.com/questions/46648645
        let tmpStreams = [];
        if (adapter_1.device.isFirefox || shouldRequestPermissions) {
            const { audio, video, streams } = await this.requestPermissions();
            deviceInUse = { ...deviceInUse, ...{ audio, video } };
            tmpStreams = streams;
        }
        try {
            const devices = await adapter_1.device.mediaDevices.enumerateDevices();
            let uniqDevices = [];
            for (const d of devices) {
                if (d.deviceId !== "" &&
                    d.label !== "" &&
                    !uniqDevices.some((u) => u.deviceId === d.deviceId && u.kind === d.kind)) {
                    uniqDevices.push(d);
                }
            }
            if (adapter_1.device.isImplements(adapter_1.Feature.PERMISSIONS)) {
                // On Safari, device.mediaDevices.enumerateDevices() returns all devices
                // even if permissions are denied so we remove them from the list manually
                if (adapter_1.device.isSafari && ((_a = this.camPermission) === null || _a === void 0 ? void 0 : _a.state) === "denied") {
                    uniqDevices = uniqDevices.filter((d) => d.kind !== "videoinput");
                }
                if (adapter_1.device.isSafari && ((_b = this.micPermission) === null || _b === void 0 ? void 0 : _b.state) === "denied") {
                    uniqDevices = uniqDevices.filter((d) => d.kind !== "audioinput");
                }
            }
            if (deviceInUse.video || deviceInUse.audio) {
                await this.handleDeviceInUse(deviceInUse, uniqDevices);
            }
            if (mediaDevicesList.length !== uniqDevices.length ||
                mediaDevicesList.some((d, i) => d.deviceId !== uniqDevices[i].deviceId ||
                    d.groupId !== uniqDevices[i].groupId ||
                    d.label !== uniqDevices[i].label ||
                    d.kind !== uniqDevices[i].kind)) {
                (0, mobx_1.runInAction)(function updateMediaDevicesList() {
                    mediaDevicesList.replace(uniqDevices);
                });
                this.emit("deviceListChanged", mediaDevicesList.slice());
            }
        }
        catch {
            this.throwError((0, errors_1.createError)(api_1.ErrorCode.EnumeratingDevices, "error enumerating devices", {}));
        }
        finally {
            if (adapter_1.device.isFirefox || adapter_1.device.isAndroidDevice) {
                for (const stream of tmpStreams) {
                    stopTracks({ stream });
                }
            }
        }
    }
    checkDevicesInUse() {
        adapter_1.device.clearInterval(this.deviceInUseInterval);
        this.logger.debug("setting checkDevicesInUse() interval");
        this.deviceInUseInterval = adapter_1.device.setInterval(async () => {
            // If no devices in use, clear interval
            if (this.devicesInUse.length === 0) {
                adapter_1.device.clearInterval(this.deviceInUseInterval);
                return;
            }
            // If there is a device in use, check if it's still in use
            for await (const d of this.devicesInUse) {
                let constraints;
                if (d.kind === "videoinput") {
                    constraints = { audio: false, video: { deviceId: d.deviceId } };
                }
                else {
                    constraints = { audio: { deviceId: d.deviceId }, video: false };
                }
                if (adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE)) {
                    let stream;
                    try {
                        stream = await adapter_1.device.mediaDevices.getUserMedia(constraints);
                        // If getUserMedia doesn't throw an Error, this deviceId is not in use anymore
                        this.logger.debug(`device no longer in use: ${d.kind}`, d);
                        // remove from devicesInUse Array
                        const index = this.devicesInUse.indexOf(d);
                        if (index > -1) {
                            (0, mobx_1.runInAction)(() => {
                                this.devicesInUse.splice(index, 1);
                            });
                        }
                        this.emit("devicesInUse", this.devicesInUse);
                        // emit event
                    }
                    catch (err) {
                        if (err instanceof Error && err.name === "NotReadableError") {
                            return;
                        }
                    }
                    finally {
                        if (stream != null)
                            stopTracks({ stream });
                    }
                }
            }
        }, 3000);
        this.addInnerDisposer(() => adapter_1.device.clearInterval(this.deviceInUseInterval));
    }
    async handleDeviceInUse(deviceInUse, uniqDevices) {
        if (deviceInUse.video) {
            for await (const d of uniqDevices) {
                if (d.kind === "videoinput" && adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE)) {
                    let stream;
                    try {
                        // If getUserMedia doesn't throw an Error, this deviceId is not in use
                        stream = await adapter_1.device.mediaDevices.getUserMedia({ audio: false, video: { deviceId: d.deviceId } });
                    }
                    catch (err) {
                        if (err instanceof Error && err.name === "NotReadableError") {
                            this.logger.debug("deviceInUse: video", d);
                            this.devicesInUse.push(d);
                        }
                    }
                    finally {
                        if (stream != null)
                            stopTracks({ stream });
                    }
                }
            }
        }
        if (deviceInUse.audio) {
            for await (const d of uniqDevices) {
                if (d.kind === "audioinput" && adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE)) {
                    let stream;
                    try {
                        // If getUserMedia doesn't throw an Error, this deviceId is not in use
                        stream = await adapter_1.device.mediaDevices.getUserMedia({ audio: { deviceId: d.deviceId }, video: false });
                    }
                    catch (err) {
                        if (err instanceof Error && err.name === "NotReadableError") {
                            this.logger.debug("deviceInUse: audio", d);
                            (0, mobx_1.runInAction)(() => {
                                this.devicesInUse.push(d);
                            });
                        }
                    }
                    finally {
                        if (stream != null)
                            stopTracks({ stream });
                    }
                }
            }
        }
        this.checkDevicesInUse();
        this.emit("devicesInUse", this.devicesInUse);
    }
    async requestPermissions() {
        var _a, _b;
        const deviceInUse = { audio: false, video: false };
        if (!adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE) || adapter_1.device.mediaDevices.enumerateDevices == null) {
            this.throwError((0, errors_1.createError)(api_1.ErrorCode.MediaDeviceNotSupported, "media device is not supported", {}));
        }
        // Have browser ask for audio and video permissions
        const tmpStreams = [];
        let hasAudioPermission = false;
        let hasVideoPermission = false;
        const requestAudioPermission = (_b = (_a = this.mediaControllerOptions) === null || _a === void 0 ? void 0 : _a.requestAudioPermission) !== null && _b !== void 0 ? _b : true;
        try {
            tmpStreams.push(await adapter_1.device.mediaDevices.getUserMedia({ audio: requestAudioPermission, video: true }));
            hasAudioPermission = requestAudioPermission;
            hasVideoPermission = true;
        }
        catch (ex) {
            // Try without video
            try {
                if (requestAudioPermission) {
                    tmpStreams.push(await adapter_1.device.mediaDevices.getUserMedia({ audio: true, video: false }));
                    hasAudioPermission = true;
                }
            }
            catch (err) {
                if (err instanceof Error) {
                    if (err.name === "NotFoundError") {
                        this.logger.warn("no audio devices found", { err: (0, errors_1.wrapError)(err) });
                        this.emit("audioNotFound");
                    }
                    else if (err.name === "NotAllowedError") {
                        this.logger.warn("audio device permission denied", { err: (0, errors_1.wrapError)(err) });
                        this.emit("audioNotAllowed");
                    }
                    else if (err.name === "NotReadableError") {
                        deviceInUse.audio = true;
                    }
                    else {
                        this.logger.warn("Audio device error", { err: (0, errors_1.wrapError)(err) });
                    }
                }
            }
            // Try without Audio
            try {
                tmpStreams.push(await adapter_1.device.mediaDevices.getUserMedia({ audio: false, video: true }));
                hasVideoPermission = true;
            }
            catch (err) {
                if (err instanceof Error) {
                    if (err.name === "NotFoundError") {
                        this.logger.warn("no video devices found", { err: (0, errors_1.wrapError)(err) });
                        this.emit("videoNotFound");
                    }
                    else if (err.name === "NotAllowedError") {
                        this.logger.warn("video device permission denied", { err: (0, errors_1.wrapError)(err) });
                        this.emit("videoNotAllowed");
                    }
                    else if (err.name === "NotReadableError") {
                        deviceInUse.video = true;
                    }
                    else {
                        this.logger.warn("Video device error", { err: (0, errors_1.wrapError)(err) });
                    }
                }
            }
        }
        finally {
            if (!adapter_1.device.isFirefox) {
                for (const stream of tmpStreams) {
                    stopTracks({ stream });
                }
            }
        }
        // We call this after we've checked for permissions so we don't want to ask for permissions twice
        await this.checkSupportSharedDevices(hasVideoPermission, hasAudioPermission);
        return { ...deviceInUse, streams: tmpStreams };
    }
}
MediaController.displayName = "MediaController";
exports.mediaController = new MediaController();
exports.optionsKey = Symbol("options");
/**
 * The controller which allows to manipulate MediaStream
 *
 * @throws {NotInitializedError} if init() was not called on the media-controller singleton
 * @throws {NotSupportedError} if the browser does not support MediaStream API
 *
 */
class MediaStreamController extends event_emitter_1.ObservableEventEmitter {
    get [exports.optionsKey]() {
        this.optionsAtom.reportObserved();
        return this.options;
    }
    get settings() {
        var _a, _b, _c, _d, _e, _f, _g;
        this.applyConstraintsAtom.reportObserved();
        const result = {};
        if (this.hasActiveAudioTrack()) {
            const track = (_a = this.source) === null || _a === void 0 ? void 0 : _a.getAudioTracks()[0];
            const meta = tracksMeta.get(track);
            if (track != null && meta != null) {
                result.audioCtx = {
                    gain: (_b = meta.gainNode) === null || _b === void 0 ? void 0 : _b.gain.value,
                    internallyEnded: meta.internallyEnded,
                    track: track.getSettings(),
                };
                result.audio = (_c = meta.originalTrack) === null || _c === void 0 ? void 0 : _c.getSettings();
            }
            else {
                result.audio = (_e = (_d = this.source) === null || _d === void 0 ? void 0 : _d.getAudioTracks()[0]) === null || _e === void 0 ? void 0 : _e.getSettings();
            }
        }
        if (this.hasActiveVideoTrack()) {
            result.video = (_g = (_f = this.source) === null || _f === void 0 ? void 0 : _f.getVideoTracks()[0]) === null || _g === void 0 ? void 0 : _g.getSettings();
        }
        return result;
    }
    get sourceUrl() {
        return URL.createObjectURL(source_1.default);
    }
    /**
     * Returns a current audio device id
     */
    get audioDeviceId() {
        var _a, _b, _c, _d;
        if (this.audioDisabled === true || this.audioDisabled === "placeholder") {
            return (_b = (_a = this.audioDeviceChanging) === null || _a === void 0 ? void 0 : _a.deviceId) !== null && _b !== void 0 ? _b : null;
        }
        return (_d = (_c = this.audioDeviceChanged) === null || _c === void 0 ? void 0 : _c.deviceId) !== null && _d !== void 0 ? _d : null;
    }
    /**
     * Set new audio video id
     *
     * @emits audioDeviceChanged if the device has changed
     */
    set audioDeviceId(value) {
        var _a, _b;
        if (value == null) {
            this.audioDeviceChanging = null;
            return;
        }
        if (audioDevices[value] == null) {
            this.emitError((0, errors_1.createError)(api_1.ErrorCode.MediaDeviceNotAvailable, "audio device not found or not available", {
                deviceId: value,
                deviceLabel: null,
                prevDeviceId: this.audioDeviceId,
                prevDeviceLabel: (_b = (_a = this.audioDeviceChanged) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : null,
                kind: "audio",
            }));
        }
        this.audioDeviceChanging = this.options.requestAudioPermission ? audioDevices[value] : null;
    }
    /**
     * Returns whether the video device is in transition
     */
    get inVideoDeviceTransition() {
        return this.sync != null && this.videoDeviceChanging !== this.videoDeviceChanged;
    }
    /**
     * Returns whether the audio device is in transition
     */
    get inAudioDeviceTransition() {
        return this.sync != null && this.audioDeviceChanging !== this.audioDeviceChanged;
    }
    /**
     * Returns a current video device id
     */
    get videoDeviceId() {
        var _a, _b, _c, _d;
        if (this.videoDisabled === true || this.videoDisabled === "placeholder") {
            return (_b = (_a = this.videoDeviceChanging) === null || _a === void 0 ? void 0 : _a.deviceId) !== null && _b !== void 0 ? _b : null;
        }
        return (_d = (_c = this.videoDeviceChanged) === null || _c === void 0 ? void 0 : _c.deviceId) !== null && _d !== void 0 ? _d : null;
    }
    /**
     * Set new audio video id
     *
     * @emits videoDeviceChanged if the device has changed
     */
    set videoDeviceId(value) {
        var _a, _b;
        if (value == null) {
            this.videoDeviceChanging = null;
            return;
        }
        if (videoDevices[value] == null) {
            this.emitError((0, errors_1.createError)(api_1.ErrorCode.MediaDeviceNotAvailable, "video device not found or not available", {
                deviceId: value,
                deviceLabel: null,
                prevDeviceId: this.videoDeviceId,
                prevDeviceLabel: (_b = (_a = this.videoDeviceChanged) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : null,
                kind: "video",
            }));
        }
        this.videoDeviceChanging = videoDevices[value];
    }
    get isScreenCaptured() {
        var _a;
        return ((_a = this.videoDeviceChanged) === null || _a === void 0 ? void 0 : _a.deviceId) === exports.VIDEO_DEVICE_SCREENCAPTURE.deviceId;
    }
    //endregion
    /**
     * @internal
     * @throws {ErrorCode.MediaStreamNotSupported} if MediaStream is not supported by the current device
     */
    constructor(options) {
        var _a, _b, _c, _d;
        super();
        //region Private Properties
        this.sync = null;
        this.audioCtx = null;
        this.optionsAtom = (0, mobx_1.createAtom)("options");
        this.applyConstraintsAtom = (0, mobx_1.createAtom)("applyConstraints");
        this.capturable = null;
        this.batchUpdates = emptyBatchUpdates();
        //endregion
        //region MobX Observable Properties
        this.audioDeviceChanged = null;
        this.videoDeviceChanged = null;
        this.audioDeviceChanging = null;
        this.videoDeviceChanging = null;
        this.videoDeviceRemoved = null;
        this.audioDeviceRemoved = null;
        this.audioMuted = false;
        this.availableResolutions = [];
        this.maxWidth = null;
        this.maxHeight = null;
        this.videoPaused = false;
        this.videoDisabled = false;
        this.audioDisabled = false;
        /**
         * Indicates the direction in which the camera producing the video track
         *
         * @type 'back' | 'front' | 'environment' | 'user'
         */
        this.facingMode = null;
        /**
         * Current volume level
         *
         * @type number
         */
        this.gain = 1;
        /**
         * This setting amplifies the volume  if true and the echo cancelation is disabled.
         *
         * @type number
         */
        this.noEchoGainAmplifier = false;
        /**
         * Current video resolution
         *
         */
        this.resolution = null;
        /**
         * Current framerate
         */
        this.frameRate = null;
        /**
         * Current aspect ratio. E.g. 16/9
         */
        this.aspectRatio = null;
        /**
         * Indicates whether echo cancellation is enabled or not.
         * @todo
         */
        this.echoCancellation = null;
        /**
         * Indicates whether noise suppression is enabled or not.
         * @todo
         */
        this.noiseSuppression = null;
        /**
         * @todo
         */
        this.autoGainControl = null;
        if (!adapter_1.device.isImplements(adapter_1.Feature.MEDIA_STREAM)) {
            this.throwError((0, errors_1.createError)(api_1.ErrorCode.MediaStreamNotSupported, "MediaStream is not supported", {}));
        }
        // Amplify gain for ios devices. With certain audio constraints on ios, audio becomes very quiet.
        this.source = new adapter_1.device.MediaStream();
        this.logger = new log_client_1.LoggerCore(common_1.PACKAGE_NAME)
            .setLoggerMeta("package", "VDC-core")
            .setLoggerMeta("client", "VDC")
            .setLoggerMeta("release", package_json_1.default.version)
            .setLoggerMeta("commitHash", package_json_1.default.commit)
            .setLoggerMeta("contextId", (_a = (0, common_1.contextId)()) !== null && _a !== void 0 ? _a : "")
            .setLoggerMeta("instanceId", (_b = (0, common_1.instanceId)()) !== null && _b !== void 0 ? _b : "")
            .setMessageAggregate("displayName", options === null || options === void 0 ? void 0 : options.displayName)
            .appendChain(MediaStreamController)
            .attachObject(this);
        this.logger.trace("constructor()", this);
        this.on("error", (err) => {
            this.logger.error(err.message);
        });
        (0, mobx_1.makeObservable)(this, {
            // observers
            audioDeviceChanged: mobx_1.observable.ref,
            videoDeviceChanged: mobx_1.observable.ref,
            audioDeviceChanging: mobx_1.observable.ref,
            videoDeviceChanging: mobx_1.observable.ref,
            audioMuted: mobx_1.observable,
            availableResolutions: mobx_1.observable,
            maxWidth: mobx_1.observable,
            maxHeight: mobx_1.observable,
            videoPaused: mobx_1.observable,
            videoDisabled: mobx_1.observable,
            audioDisabled: mobx_1.observable,
            facingMode: mobx_1.observable,
            gain: mobx_1.observable,
            resolution: mobx_1.observable,
            frameRate: mobx_1.observable,
            aspectRatio: mobx_1.observable,
            echoCancellation: mobx_1.observable,
            noiseSuppression: mobx_1.observable,
            autoGainControl: mobx_1.observable,
            source: mobx_1.observable.ref,
            // computed
            audioDeviceId: mobx_1.computed,
            videoDeviceId: mobx_1.computed,
            isScreenCaptured: mobx_1.computed,
            sourceUrl: mobx_1.computed,
            settings: mobx_1.computed,
            // actions
            toggleCamera: mobx_1.action,
            toggleMic: mobx_1.action,
            onDeviceListChanged: mobx_1.action,
        });
        // this.prepareStream()
        // using JSON.parse(JSON.stringify()) to create a deep copy of defaultMediaStreamControllerOptions
        this.options = JSON.parse(JSON.stringify(defaultMediaStreamControllerOptions));
        if (options != null) {
            this.setOptions({ ...defaultMediaStreamControllerOptions, ...options });
            if (((_c = options === null || options === void 0 ? void 0 : options.capturable) === null || _c === void 0 ? void 0 : _c.element) != null && adapter_1.device.supportsMediaStreamCapture((_d = options === null || options === void 0 ? void 0 : options.capturable) === null || _d === void 0 ? void 0 : _d.element)) {
                this.capturable = options.capturable;
                this.videoDeviceId = exports.VIDEO_DEVICE_CAPTURABLE.deviceId;
                this.applyNewDevices();
            }
        }
        if (this.options.noEchoGainAmplifier) {
            this.noEchoGainAmplifier = true;
        }
        this.setupMobxBatching();
        exports.mediaController.on("deviceListChanged", this.onDeviceListChanged);
        this.addInnerDisposer(() => {
            this.close();
        });
    }
    //region MobX Actions
    /**
     * Returns whether this exists an active stream on the controller
     */
    hasActiveStream() {
        const activeAudioTrack = this.hasActiveAudioTrack();
        const activeVideoTrack = this.hasActiveAudioTrack();
        return activeAudioTrack || activeVideoTrack;
    }
    /**
     * Returns whether this exists an active video track on the controller
     */
    hasActiveVideoTrack() {
        const { videoDeviceChanged, videoDisabled } = this;
        return videoDeviceChanged != null && videoDisabled === false;
    }
    /**
     * Returns whether this exists an active audio track on the controller
     */
    hasActiveAudioTrack() {
        return this.audioDeviceChanged != null && this.audioDisabled === false;
    }
    /**
     * Toggle the video paused attribute
     */
    toggleCamera() {
        this.videoPaused = !this.videoPaused;
    }
    /**
     * Toggle the audio muted attribute
     */
    toggleMic() {
        this.audioMuted = !this.audioMuted;
    }
    //endregion
    onDeviceListChanged(devices) {
        if (this.videoDeviceChanged != null && !devices.some((d) => { var _a; return d.deviceId === ((_a = this.videoDeviceChanged) === null || _a === void 0 ? void 0 : _a.deviceId); })) {
            const vd = devices.filter((d) => d.kind === "videoinput");
            if (vd.length > 0) {
                [this.videoDeviceChanging] = vd;
            }
            else {
                this.videoDeviceChanging = null;
            }
        }
        if (this.audioDeviceChanged != null && !devices.some((d) => { var _a; return d.deviceId === ((_a = this.audioDeviceChanged) === null || _a === void 0 ? void 0 : _a.deviceId); })) {
            const ad = devices.filter((d) => d.kind === "audioinput");
            if (ad.length > 0) {
                [this.audioDeviceChanging] = ad;
            }
            else {
                this.audioDeviceChanging = null;
            }
        }
    }
    setupMobxBatching() {
        // mobx watchers
        const opts = {
            scheduler: this.batchUpdatesScheduler,
            name: "MediaStreamController(batch update)",
        };
        this.addInnerDisposer((0, mobx_1.reaction)(() => {
            return this.videoDeviceChanging;
        }, () => {
            this.batchUpdates.videoDevice = true;
        }, opts));
        this.addInnerDisposer((0, mobx_1.reaction)(() => this.audioDeviceChanging, () => {
            this.batchUpdates.audioDevice = true;
        }, opts));
        this.addInnerDisposer((0, mobx_1.reaction)(() => this.videoDisabled, () => {
            this.batchUpdates.videoDevice = true;
        }, opts));
        this.addInnerDisposer((0, mobx_1.reaction)(() => this.audioDisabled, () => {
            this.batchUpdates.audioDevice = true;
        }, opts));
        this.addInnerDisposer((0, mobx_1.reaction)(() => this.gain, () => {
            this.batchUpdates.gain = true;
        }, opts));
        this.addInnerDisposer((0, mobx_1.reaction)(() => this.videoPaused, () => {
            this.batchUpdates.videoPaused = true;
        }, opts));
        this.addInnerDisposer((0, mobx_1.reaction)(() => this.audioMuted, () => {
            this.batchUpdates.audioMuted = true;
        }, opts));
        this.addInnerDisposer((0, mobx_1.reaction)(() => {
            return [
                this.facingMode,
                this.aspectRatio,
                this.frameRate,
                this.resolution,
                this.noiseSuppression,
                this.autoGainControl,
                this.echoCancellation,
            ];
        }, () => {
            this.batchUpdates.constraints = true;
        }, opts));
    }
    batchUpdatesScheduler(run, ...args) {
        this.batchUpdates.changes.push(run);
        if (this.batchUpdates.timeout == null) {
            this.batchUpdates.timeout = adapter_1.device.setInterval(() => {
                var _a;
                if (this.sync == null) {
                    adapter_1.device.clearInterval((_a = this.batchUpdates.timeout) !== null && _a !== void 0 ? _a : undefined);
                }
                else {
                    return;
                }
                this.batchUpdates.changes.forEach((u) => u());
                const updates = this.batchUpdates;
                this.batchUpdates = emptyBatchUpdates();
                if (updates.audioDevice || updates.videoDevice) {
                    this.applyNewDevices();
                    // no need handle other changes since changing
                    // audio/video triggers `prepareStream()`
                    return;
                }
                if (updates.constraints && !adapter_1.device.isImplements(adapter_1.Feature.APPLY_CONSTRAINTS)) {
                    // if applyConstraints() is not implemented
                    // then it triggers prepareStream() also
                    this.applyConstraints();
                    return;
                }
                if (updates.audioMuted) {
                    this.applyAudioMute();
                }
                if (updates.videoPaused) {
                    this.applyVideoPause();
                }
                if (updates.gain) {
                    this.applyGain();
                }
                if (updates.constraints) {
                    this.applyConstraints();
                }
            }, MOBX_REACT_DELAY);
        }
    }
    setOptions(options) {
        let constraintsChanged = false;
        if (options.replaceTracks != null) {
            this.options.replaceTracks = options.replaceTracks;
        }
        if (options.requestAudioPermission != null) {
            this.options.requestAudioPermission = options.requestAudioPermission;
        }
        if (options.defaultConstraints != null) {
            this.options.defaultConstraints = { ...options.defaultConstraints };
            constraintsChanged = true;
        }
        if (options.fallbackConstraints !== undefined) {
            this.options.fallbackConstraints =
                options.fallbackConstraints == null ? null : { ...options.fallbackConstraints };
            constraintsChanged = true;
        }
        if (options.capturable !== undefined) {
            this.capturable = options.capturable;
        }
        if (constraintsChanged) {
            this.applyConstraints();
        }
        this.optionsAtom.reportChanged();
    }
    constraints({ withAudio = true, withVideo = true, }) {
        return buildStreamConstraints({
            defaults: this.options.defaultConstraints,
            audioDeviceInfo: !this.options.requestAudioPermission || !withAudio || this.audioDisabled === true
                ? null
                : this.audioDeviceChanging,
            videoDeviceInfo: this.videoDisabled === true || !withVideo ? null : this.videoDeviceChanging,
            overrides: {
                aspectRatio: this.aspectRatio,
                autoGainControl: this.autoGainControl,
                echoCancellation: this.echoCancellation,
                facingMode: this.facingMode,
                frameRate: this.frameRate,
                noiseSuppression: this.noiseSuppression,
                resolution: this.maxHeight != null && this.maxHeight !== 0 && this.resolution == null
                    ? { min: 240, ideal: this.maxHeight, max: this.maxHeight }
                    : this.resolution,
            },
        });
    }
    fallbackConstraints({ withAudio = true, withVideo = true, }) {
        return this.options.fallbackConstraints == null
            ? null
            : buildStreamConstraints({
                defaults: this.options.fallbackConstraints,
                audioDeviceInfo: !this.options.requestAudioPermission || !withAudio || this.audioDisabled === true
                    ? null
                    : this.audioDeviceChanging,
                videoDeviceInfo: this.videoDisabled === true || !withVideo ? null : this.videoDeviceChanging,
                overrides: {
                    aspectRatio: this.aspectRatio,
                    autoGainControl: this.autoGainControl,
                    echoCancellation: this.echoCancellation,
                    facingMode: this.facingMode,
                    frameRate: this.frameRate,
                    noiseSuppression: this.noiseSuppression,
                    resolution: this.maxHeight != null && this.maxHeight !== 0 && this.resolution == null
                        ? { min: 240, ideal: this.maxHeight, max: this.maxHeight }
                        : this.resolution, // should this resolution property behave the same as the regular constraints??
                },
            });
    }
    activeConstraints() {
        var _a, _b, _c, _d, _e, _f;
        this.applyConstraintsAtom.reportObserved();
        const res = {};
        try {
            if (this.hasActiveVideoTrack()) {
                res.video = (_c = (_b = (_a = this.source) === null || _a === void 0 ? void 0 : _a.getVideoTracks()) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.getConstraints();
            }
            if (this.hasActiveAudioTrack()) {
                const track = (_e = (_d = this.source) === null || _d === void 0 ? void 0 : _d.getAudioTracks()) === null || _e === void 0 ? void 0 : _e[0];
                if (track != null) {
                    const orig = (_f = tracksMeta.get(track)) === null || _f === void 0 ? void 0 : _f.originalTrack;
                    res.audio = orig != null ? orig.getConstraints() : track.getConstraints();
                }
            }
        }
        catch (err) {
            this.logger.warn("unable to detect active constaints");
        }
        return res;
    }
    activeCapabilities() {
        var _a, _b, _c, _d, _e, _f;
        const res = {};
        try {
            if (this.hasActiveVideoTrack()) {
                res.video = (_c = (_b = (_a = this.source) === null || _a === void 0 ? void 0 : _a.getVideoTracks()) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.getCapabilities();
            }
            if (this.hasActiveAudioTrack()) {
                const track = (_e = (_d = this.source) === null || _d === void 0 ? void 0 : _d.getAudioTracks()) === null || _e === void 0 ? void 0 : _e[0];
                if (track != null) {
                    const orig = (_f = tracksMeta.get(track)) === null || _f === void 0 ? void 0 : _f.originalTrack;
                    res.audio = orig != null ? orig.getCapabilities() : track.getCapabilities();
                }
            }
        }
        catch (err) {
            this.logger.warn("unable to detect active capabilities");
        }
        return res;
    }
    stopTracksInternal({ stream, audio, video }) {
        const removeEventListenerCb = (track) => {
            if (track.kind === "audio") {
                track.removeEventListener("ended", this.handleAudioTrackEnded);
            }
            else {
                track.removeEventListener("ended", this.handleVideoTrackEnded);
            }
        };
        stopTracks({ stream, audio, video, removeEventListenerCb });
    }
    applyVideoPause() {
        var _a;
        const { videoPaused } = this;
        // it will be better to implement screencapture with dedicated stream
        if (((_a = this.videoDeviceChanged) === null || _a === void 0 ? void 0 : _a.deviceId) === exports.VIDEO_DEVICE_SCREENCAPTURE.deviceId) {
            return;
        }
        this.source.getVideoTracks().forEach((t) => {
            if (videoPaused) {
                this.logger.debug("pause video track", { trackId: t.id });
            }
            else {
                this.logger.debug("unpause video track", { trackId: t.id });
            }
            t.enabled = !videoPaused;
        });
    }
    applyAudioMute() {
        const { audioMuted } = this;
        this.source.getAudioTracks().forEach((t) => {
            if (audioMuted) {
                this.logger.debug("disable audio track", { trackId: t.id });
            }
            else {
                this.logger.debug("enable audio track", { trackId: t.id });
            }
            t.enabled = !audioMuted;
        });
    }
    getVideoDevice() {
        if (this.videoDisabled === "placeholder") {
            return exports.VIDEO_DEVICE_CAPTURABLE;
        }
        if (this.videoDisabled) {
            return null;
        }
        return this.videoDeviceChanging;
    }
    async applyNewDevices() {
        const { replaceTracks } = this.options;
        const videoDevice = this.getVideoDevice();
        const audioDevice = this.audioDisabled === true || this.audioDisabled === "placeholder" ? null : this.audioDeviceChanging;
        const videoDeviceOld = this.videoDeviceChanged;
        const audioDeviceOld = this.audioDeviceChanged;
        const videoChanged = videoDevice !== this.videoDeviceChanged;
        const audioChanged = this.audioDeviceChanging !== this.audioDeviceChanged;
        if (videoDevice == null && audioDevice == null) {
            (0, mobx_1.action)("applyNewDevices", () => {
                if (adapter_1.device.isImplements(adapter_1.Feature.MEDIA_STREAM)) {
                    this.audioDeviceChanged = audioDevice;
                    this.videoDeviceChanged = videoDevice;
                    this.stopTracksInternal({ stream: this.source });
                    this.source = new adapter_1.device.MediaStream();
                }
            })();
            return;
        }
        this.logger.debug("preparing new devices", {
            videoDevice,
            audioDevice,
            videoChanged,
            audioChanged,
            replaceTracks,
        });
        let newStream = null;
        const curStream = this.source;
        if (replaceTracks) {
            try {
                // that's ok to create a separate stream
                // because replaceTracks is true
                await this.handleUpdateResolutions({ videoDevice, videoChanged, videoDeviceOld });
                newStream = await this.lockAndPrepareStream(videoDevice, this.options.defaultLockPolicy, this.constraints({ withAudio: audioChanged, withVideo: videoChanged }), this.fallbackConstraints({ withAudio: audioChanged, withVideo: videoChanged }));
                this.stopTracksInternal({ stream: curStream, audio: audioChanged, video: videoChanged });
            }
            catch (err) {
                this.handleNewStreamError({
                    newStream,
                    err,
                    deviceState: {
                        videoDevice,
                        audioDevice,
                        videoDeviceOld,
                        audioDeviceOld,
                        videoChanged,
                        audioChanged,
                    },
                });
                return;
            }
        }
        else {
            try {
                // stop only tracks for changed devices
                this.stopTracksInternal({ stream: curStream, audio: audioChanged, video: videoChanged });
                // the only moment to calculate resolutions
                // when all old streams were stopped
                // but new ones are not started yet
                await this.handleUpdateResolutions({ videoDevice, videoChanged, videoDeviceOld });
                newStream = await this.lockAndPrepareStream(videoDevice, this.options.defaultLockPolicy, 
                // generate constraints only for changed devices
                // because we stopped old tracks only for them
                this.constraints({ withAudio: audioChanged, withVideo: videoChanged }), this.fallbackConstraints({ withAudio: audioChanged, withVideo: videoChanged }));
            }
            catch (err) {
                this.handleNewStreamError({
                    newStream,
                    err,
                    deviceState: {
                        videoDevice,
                        audioDevice,
                        videoDeviceOld,
                        audioDeviceOld,
                        videoChanged,
                        audioChanged,
                    },
                });
                return;
            }
        }
        // move all unchanged tracks from previous stream as is
        for (const at of curStream.getTracks()) {
            curStream.removeTrack(at);
            newStream.addTrack(at);
        }
        // to makes ts feel good
        const finalStream = newStream;
        (0, mobx_1.action)("applyNewDevices", () => {
            this.audioDeviceChanged = audioDevice;
            this.videoDeviceChanged = videoDevice;
            this.source = finalStream;
            this.logger.info("applied new devices", { videoDevice, audioDevice });
        })();
    }
    async handleNewStreamError({ newStream, err, deviceState, }) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const { videoDevice, audioDevice, videoDeviceOld, audioDeviceOld, videoChanged, audioChanged } = deviceState;
        this.stopTracksInternal({ stream: newStream });
        if (err instanceof Error && err.name === "NotReadableError") {
            await exports.mediaController.handleDeviceInUse({ audio: true, video: true }, mediaDevicesList);
            this.emitError((0, errors_1.createError)(api_1.ErrorCode.DeviceInUse, "device(s) already in use", err));
            // restore previous stream if possible
            if (this.sync == null) {
                (0, mobx_1.action)("rollbackDevices", () => {
                    this.videoDeviceChanging = videoDeviceOld;
                    this.audioDeviceChanging = audioDeviceOld;
                })();
            }
            return;
        }
        if (videoChanged) {
            this.emitError((0, errors_1.createError)(api_1.ErrorCode.MediaDeviceChangingFailed, "unable to change video device", {
                deviceId: (_a = videoDevice === null || videoDevice === void 0 ? void 0 : videoDevice.deviceId) !== null && _a !== void 0 ? _a : null,
                deviceLabel: (_b = videoDevice === null || videoDevice === void 0 ? void 0 : videoDevice.label) !== null && _b !== void 0 ? _b : null,
                prevDeviceId: (_c = videoDeviceOld === null || videoDeviceOld === void 0 ? void 0 : videoDeviceOld.deviceId) !== null && _c !== void 0 ? _c : null,
                prevDeviceLabel: (_d = videoDeviceOld === null || videoDeviceOld === void 0 ? void 0 : videoDeviceOld.label) !== null && _d !== void 0 ? _d : null,
                kind: "video",
            }, err));
        }
        if (audioChanged) {
            this.emitError((0, errors_1.createError)(api_1.ErrorCode.MediaDeviceChangingFailed, "unable to change audio device", {
                deviceId: (_e = audioDevice === null || audioDevice === void 0 ? void 0 : audioDevice.deviceId) !== null && _e !== void 0 ? _e : null,
                deviceLabel: (_f = audioDevice === null || audioDevice === void 0 ? void 0 : audioDevice.label) !== null && _f !== void 0 ? _f : null,
                prevDeviceId: (_g = audioDeviceOld === null || audioDeviceOld === void 0 ? void 0 : audioDeviceOld.deviceId) !== null && _g !== void 0 ? _g : null,
                prevDeviceLabel: (_h = audioDeviceOld === null || audioDeviceOld === void 0 ? void 0 : audioDeviceOld.label) !== null && _h !== void 0 ? _h : null,
                kind: "audio",
            }, err));
        }
        // deprecated
        this.emit("changeDevicesError", {
            err: err,
            audio: {
                old: audioDeviceOld,
                new: audioDevice,
            },
            video: {
                old: videoDeviceOld,
                new: videoDevice,
            },
        });
        // restore previous stream if possible
        if (this.sync == null) {
            (0, mobx_1.action)("rollbackDevices", () => {
                this.videoDeviceChanging = videoDeviceOld;
                this.audioDeviceChanging = audioDeviceOld;
            })();
        }
    }
    async handleUpdateResolutions(deviceState) {
        var _a, _b, _c, _d;
        const { videoDevice, videoDeviceOld, videoChanged } = deviceState;
        if (videoChanged && videoDevice !== exports.VIDEO_DEVICE_SCREENCAPTURE && videoDevice !== exports.VIDEO_DEVICE_CAPTURABLE) {
            try {
                await this.updateResolutions(videoDevice);
            }
            catch (err) {
                this.emitError((0, errors_1.createError)(api_1.ErrorCode.UpdateResolutionsFailed, "unable to update available resolutions", {
                    kind: "video",
                    deviceId: (_a = videoDevice === null || videoDevice === void 0 ? void 0 : videoDevice.deviceId) !== null && _a !== void 0 ? _a : null,
                    deviceLabel: (_b = videoDevice === null || videoDevice === void 0 ? void 0 : videoDevice.label) !== null && _b !== void 0 ? _b : null,
                    prevDeviceId: (_c = videoDeviceOld === null || videoDeviceOld === void 0 ? void 0 : videoDeviceOld.deviceId) !== null && _c !== void 0 ? _c : null,
                    prevDeviceLabel: (_d = videoDeviceOld === null || videoDeviceOld === void 0 ? void 0 : videoDeviceOld.label) !== null && _d !== void 0 ? _d : null,
                }, err));
            }
        }
    }
    async getDeviceResolutions({ videoDevice, deviceInUse, }) {
        const aspectRatio = typeof this.aspectRatio === "number" ? this.aspectRatio : 16 / 9;
        let maxWidth = null;
        let maxHeight = null;
        let deviceResolutions = null;
        let resolutionsInUse = false;
        if ((videoDevice === null || videoDevice === void 0 ? void 0 : videoDevice.deviceId) == null) {
            this.logger.debug("Unable to get best resolution stream, deviceId undefined");
            return { maxHeight, maxWidth, availableResolutions: [], resolutionsInUse };
        }
        this.logger.debug(`Getting best resolution stream for ${videoDevice.deviceId}`, {
            newVersion: CURRENT_DEVICE_RESOLUTION_VERSION,
        });
        // a new device, so we need a new stream to calculate resolution
        // not using constraints generation (UNLESS FIREFOX) because we need
        // a best mediaStream for selected deviceId
        let videoConstraints = { deviceId: { exact: videoDevice.deviceId } };
        if (adapter_1.device.isFirefox) {
            videoConstraints = { ...videoConstraints, height: { ideal: 4000 } };
        }
        const bestResolutionStream = await this.lockAndPrepareStream(videoDevice, api_1.ExistsStreamPolicy.error, {
            video: videoConstraints,
        });
        deviceResolutions = calculateDeviceResolutions({
            stream: bestResolutionStream,
            logger: this.logger,
            deviceInUse,
        });
        this.stopTracksInternal({ stream: bestResolutionStream });
        // if the device is in use, we want to use the stored resolution options (bc what is being returned
        // from the browser could be limited)
        [maxWidth, maxHeight] =
            deviceResolutionsCache[videoDevice.deviceId] != null && deviceInUse
                ? deviceResolutionsCache[videoDevice.deviceId]
                : deviceResolutions;
        // The aspect ratio of the video affects the availableResolutions/ constraints that can be applied
        const availableResolutions = possibleResolutions.filter((h) => maxWidth != null && maxHeight != null && h <= maxHeight && h * aspectRatio <= maxWidth);
        if (deviceInUse) {
            const [maxWidthCurrent, maxHeightCurrent] = deviceResolutions;
            // The aspect ratio of the video affects the availableResolutions/ constraints that can be applied
            const currentAvailableResolutions = possibleResolutions.filter((h) => maxWidthCurrent != null &&
                maxHeightCurrent != null &&
                h <= maxHeightCurrent &&
                h * aspectRatio <= maxWidthCurrent);
            if (currentAvailableResolutions.length < availableResolutions.length) {
                resolutionsInUse = true;
                this.emitError((0, errors_1.createError)(api_1.ErrorCode.ResolutionsInUse, "device is already in use by another application and some resolutions are unavailable", {
                    videoDevice,
                    allAvailableResolutions: availableResolutions,
                    currentAvailableResolutions,
                }));
            }
        }
        return { maxWidth, maxHeight, availableResolutions, deviceResolutions, resolutionsInUse };
    }
    async updateResolutions(videoDevice) {
        const deviceId = videoDevice === null || videoDevice === void 0 ? void 0 : videoDevice.deviceId;
        // must never used in ff/react-native with running streams
        if (!this.options.replaceTracks && this.source.getVideoTracks().some((t) => t.readyState === "live")) {
            throw new Error("attempt to get resolutions with replaceTracks enabled and running stream");
        }
        if (deviceId == null) {
            this.logger.debug("Unable to get best resolution stream, deviceId undefined");
            (0, mobx_1.runInAction)(() => {
                this.maxWidth = 0;
                this.maxHeight = 0;
                this.availableResolutions = [];
            });
            return;
        }
        if (adapter_1.device.isImplements(adapter_1.Feature.LOCAL_STORAGE)) {
            let resolution;
            const data = adapter_1.device.localStorage.getItem(deviceResolutionCacheKey(deviceId));
            if (data != null) {
                try {
                    resolution = JSON.parse(data);
                    if (Array.isArray(resolution)) {
                        deviceResolutionsCache[deviceId] = resolution;
                    }
                }
                catch (err) {
                    const msg = err instanceof Error ? err.message : "unknown error";
                    this.logger.error("unable to parse device resolution settings", {
                        data,
                        err: msg,
                    });
                }
            }
        }
        const { maxWidth, maxHeight, availableResolutions, deviceResolutions } = await this.getDeviceResolutions({
            videoDevice,
            deviceInUse: false,
        });
        if (deviceResolutionsCache[deviceId] == null && deviceResolutions != null) {
            deviceResolutionsCache[deviceId] = deviceResolutions;
            if (adapter_1.device.isImplements(adapter_1.Feature.LOCAL_STORAGE)) {
                adapter_1.device.localStorage.setItem(deviceResolutionCacheKey(deviceId), JSON.stringify(deviceResolutionsCache[deviceId]));
            }
        }
        (0, mobx_1.runInAction)(() => {
            this.maxWidth = maxWidth;
            this.maxHeight = maxHeight;
            this.availableResolutions = availableResolutions;
        });
    }
    async applyConstraintsVideo(vt, fallbackVideo, defaultVideo) {
        var _a;
        if (adapter_1.device.isImplements(adapter_1.Feature.APPLY_CONSTRAINTS)) {
            this.logger.debug("apply video constraints", { defaultVideo, fallbackVideo });
            //This property is not supported on safari and will throw and overconstrained error.
            if (adapter_1.device.isSafari) {
                delete defaultVideo.deviceId;
            }
            try {
                await adapter_1.device.applyConstraints(vt, defaultVideo);
            }
            catch (err) {
                this.logger.warn("unable to apply video constraints", { defaultVideo });
                const inner = err instanceof Error || (0, dom_exception_1.isErrorLike)(err) ? err : null;
                const active = this.activeConstraints();
                const diffConstraints = (0, deep_object_diff_1.diff)(defaultVideo, (_a = active.video) !== null && _a !== void 0 ? _a : {});
                this.emit("videoConstraintsError", {
                    diff: diffConstraints,
                    isFallback: false,
                    constraints: defaultVideo,
                    err: (0, errors_1.wrapError)(inner),
                });
                if (fallbackVideo != null && typeof fallbackVideo !== "boolean") {
                    this.logger.debug("trying fallback video constraints", { fallbackVideo });
                    adapter_1.device.applyConstraints(vt, fallbackVideo).catch((err2) => {
                        var _a;
                        const inner2 = err2 instanceof Error || (0, dom_exception_1.isErrorLike)(err2) ? err2 : null;
                        const active2 = this.activeConstraints();
                        const diffConstraints2 = (0, deep_object_diff_1.diff)(fallbackVideo, (_a = active2.video) !== null && _a !== void 0 ? _a : {});
                        if (inner2 != null && (0, dom_exception_1.isOverconstrainedError)(inner2) && inner2.constraint === "height") {
                            this.logger.error("Unable to apply selected resolution, please select a resolution supported by your current video device.", {
                                fallbackConstraints: fallbackVideo,
                                defaultConstraints: defaultVideo,
                                trackId: vt.id,
                                trackType: vt.kind,
                                err: (0, errors_1.wrapError)(inner2),
                            });
                            this.emit("videoConstraintsError", {
                                diff: diffConstraints2,
                                isFallback: true,
                                constraints: fallbackVideo,
                                err: (0, errors_1.wrapError)(inner2),
                            });
                            // Device is probably already being used and availableResolutions is affected
                            this.getDeviceResolutions({ videoDevice: this.getVideoDevice(), deviceInUse: true });
                        }
                        else {
                            this.logger.error("Unable to apply video constraints. Try restarting the stream.", {
                                fallbackConstraints: fallbackVideo,
                                defaultConstraints: defaultVideo,
                                trackId: vt.id,
                                trackType: vt.kind,
                                err: (0, errors_1.wrapError)(inner2),
                            });
                            this.emit("videoConstraintsError", {
                                diff: diffConstraints2,
                                isFallback: true,
                                constraints: fallbackVideo,
                                err: (0, errors_1.wrapError)(inner2),
                            });
                        }
                    });
                }
            }
        }
    }
    async applyConstraintsAudio(at, defaultAudio, fallbackAudio) {
        var _a, _b;
        if (adapter_1.device.isImplements(adapter_1.Feature.APPLY_CONSTRAINTS)) {
            try {
                //This property is not supported on safari and will throw and overconstrained error.
                if (adapter_1.device.isSafari) {
                    delete defaultAudio.deviceId;
                }
                await adapter_1.device.applyConstraints(at, defaultAudio);
            }
            catch (err) {
                this.logger.warn("unable to apply constraints", { defaultAudio });
                const inner = err instanceof Error || (0, dom_exception_1.isErrorLike)(err) ? err : null;
                const active = this.activeConstraints();
                const diffConstraints = (0, deep_object_diff_1.diff)(defaultAudio, (_a = active.audio) !== null && _a !== void 0 ? _a : {});
                this.emit("audioConstraintsError", {
                    diff: diffConstraints,
                    isFallback: false,
                    constraints: defaultAudio,
                    err: (0, errors_1.wrapError)(inner),
                });
                if (fallbackAudio != null && typeof fallbackAudio !== "boolean") {
                    this.logger.debug("trying fallback apply constraints", { fallbackAudio });
                    const active2 = this.activeConstraints();
                    const diffConstraints2 = (0, deep_object_diff_1.diff)(fallbackAudio, (_b = active2.audio) !== null && _b !== void 0 ? _b : {});
                    adapter_1.device.applyConstraints(at, fallbackAudio).catch((err2) => {
                        const inner2 = err2 instanceof Error || (0, dom_exception_1.isErrorLike)(err2) ? err2 : null;
                        this.logger.error("unable to apply audio constraints. restart stream", {
                            fallbackConstraints: fallbackAudio,
                            defaultConstraints: defaultAudio,
                            trackId: at.id,
                            trackType: at.kind,
                            err: (0, errors_1.wrapError)(inner2),
                        });
                        this.emit("audioConstraintsError", {
                            diff: diffConstraints2,
                            isFallback: true,
                            constraints: fallbackAudio,
                            err: (0, errors_1.wrapError)(inner2),
                        });
                    });
                }
            }
        }
    }
    async applyConstraints() {
        if (this.audioDeviceChanging == null && this.videoDeviceChanging == null) {
            return;
        }
        if (!adapter_1.device.isImplements(adapter_1.Feature.APPLY_CONSTRAINTS)) {
            this.stopTracksInternal({ stream: this.source });
            try {
                this.source = await this.lockAndPrepareStream(this.videoDeviceChanging, this.options.defaultLockPolicy, this.constraints({ withAudio: true, withVideo: true }), this.fallbackConstraints({ withAudio: true, withVideo: true }));
            }
            catch (err) {
                this.emitError((0, errors_1.createError)(api_1.ErrorCode.ApplyingConstraintsFailed, "applying constraints failed", {
                    constraints: this.constraints({ withAudio: true, withVideo: true }),
                    fallbackConstraints: this.fallbackConstraints({ withAudio: true, withVideo: true }),
                }, err));
            }
            return;
        }
        const curStream = this.source;
        const constraints = this.constraints({ withAudio: true, withVideo: true });
        const fallbackConstraints = this.fallbackConstraints({ withAudio: true, withVideo: true });
        const waitGroup = [];
        const defaultVideo = constraints.video;
        const fallbackVideo = fallbackConstraints === null || fallbackConstraints === void 0 ? void 0 : fallbackConstraints.video;
        const defaultAudio = constraints.audio;
        const fallbackAudio = fallbackConstraints === null || fallbackConstraints === void 0 ? void 0 : fallbackConstraints.audio;
        if (defaultVideo != null && typeof defaultVideo !== "boolean") {
            waitGroup.push(...curStream
                .getVideoTracks()
                .filter((t) => t.readyState !== "ended")
                .map((t) => this.applyConstraintsVideo(t, fallbackVideo, defaultVideo)));
        }
        if (defaultAudio != null && typeof defaultAudio !== "boolean") {
            waitGroup.push(...curStream
                .getAudioTracks()
                .map((t) => { var _a; return (((_a = tracksMeta.get(t)) === null || _a === void 0 ? void 0 : _a.originalTrack) != null ? tracksMeta.get(t).originalTrack : t); })
                .filter((t) => t)
                .map((t) => this.applyConstraintsAudio(t, defaultAudio, fallbackAudio)));
        }
        await Promise.all(waitGroup);
        this.applyConstraintsAtom.reportChanged();
    }
    /**
     * Check for `noiseSuppression` support.
     * @description Check if user's device supports noise suppression.
     * @example <caption>Example usage of supportsNoiseSuppression.</caption>
     * supportsNoiseSuppression();
     * // Returns true if user device supports noiseSuppression.
     *
     * @see noiseSuppression
     */
    supportsNoiseSuppression() {
        var _a;
        if (adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE)) {
            try {
                return (_a = adapter_1.device.mediaDevices.getSupportedConstraints().noiseSuppression) !== null && _a !== void 0 ? _a : false;
            }
            catch (e) {
                return false;
            }
        }
        return false;
    }
    /**
     * Check for `echoCancellation` support.
     * @description Check if user's device supports echo cancellation.
     * @example <caption>Example usage of supportsEchoCancellation.</caption>
     * supportsEchoCancellation();
     * // Returns `true` if user device supports echoCancellation.
     *
     * @see echoCancellation
     *
     * @todo This method is throwing an error w/ @computed.
     */
    supportsEchoCancellation() {
        var _a;
        if (adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE)) {
            try {
                return (_a = adapter_1.device.mediaDevices.getSupportedConstraints().echoCancellation) !== null && _a !== void 0 ? _a : false;
            }
            catch (e) {
                return false;
            }
        }
        return false;
    }
    async applyGain() {
        const audioTrack = this.source.getAudioTracks()[0];
        const meta = tracksMeta.get(audioTrack);
        if ((meta === null || meta === void 0 ? void 0 : meta.gainNode) != null) {
            const constraints = getConstraints(audioTrack);
            if (this.noEchoGainAmplifier && constraints.echoCancellation === false) {
                meta.gainNode.gain.value = this.gain * AUDIO_GAIN_MULTIPLIER;
            }
            else {
                meta.gainNode.gain.value = this.gain;
            }
            this.applyConstraintsAtom.reportChanged();
        }
    }
    /**
     * @throws {ErrorCode.MediaStreamPreparingConflict}
     */
    async lockAndPrepareStream(videoDevice, policy, constraints, fallbackConstraints) {
        const kind = 
        // eslint-disable-next-line no-nested-ternary
        videoDevice === exports.VIDEO_DEVICE_SCREENCAPTURE
            ? "screencapture"
            : videoDevice === exports.VIDEO_DEVICE_CAPTURABLE
                ? "captureable"
                : "media";
        // we'll get 100% error with { audio: false, video: false} constraints
        // so just return an empty MediaStream instead
        if (constraints.audio === false && constraints.video === false && adapter_1.device.isImplements(adapter_1.Feature.MEDIA_STREAM)) {
            return new adapter_1.device.MediaStream();
        }
        this.logger.trace("lockAndPrepareStream()", {
            policy: api_1.ExistsStreamPolicy[policy],
            constraints,
        });
        if (this.sync != null) {
            switch (policy) {
                case api_1.ExistsStreamPolicy.error:
                    this.throwError((0, errors_1.createError)(api_1.ErrorCode.MediaStreamPreparingConflict, "MediaStream is preparing already", {}));
                    break;
                case api_1.ExistsStreamPolicy.stale:
                    this.logger.debug("stream is preparing already, waiting");
                    return this.sync;
                case api_1.ExistsStreamPolicy.wait:
                    await this.sync;
                    return new Promise((resolve, reject) => {
                        adapter_1.device.setTimeout(() => {
                            this.lockAndPrepareStream(videoDevice, policy, constraints, fallbackConstraints)
                                .then(resolve)
                                .catch(reject);
                        }, this.options.waitingDelay);
                    });
                case api_1.ExistsStreamPolicy.ignore:
                    this.sync.then((stream) => {
                        this.stopTracksInternal({ stream });
                    });
                    break; // continue prepare stream
                default:
                    throw new Error(`Unknown policy MediaStream ${policy}`);
            }
        }
        this.sync = this.prepareStream(kind, constraints).catch((err) => {
            if (fallbackConstraints != null && kind === "media") {
                this.logger.warn("constraints failed. using fallback constraints", {
                    constraints,
                    fallbackConstraints,
                    err,
                });
                return this.prepareStream(kind, fallbackConstraints);
            }
            throw err;
        });
        return this.sync.finally(() => {
            this.sync = null;
        });
    }
    /**
     * Prepares a new stream with current constraints and gain processing
     * Throws `VideoClientError` if failed
     */
    async prepareStream(kind, constraints) {
        var _a, _b;
        this.logger.trace("prepareStream() start", { constraints });
        let newStream = null;
        // If using screenCapture, get ScreenCaptureStream and set stream or append tracks
        // depending on whether audio is in use or not.
        if (kind === "screencapture") {
            newStream = await this.prepareScreenCaptureStream({
                audio: constraints.audio,
                video: this.options.defaultConstraints.screencapture,
            });
        }
        else if (kind === "captureable") {
            newStream = await this.prepareCapturableStream({
                audio: constraints.audio,
            });
        }
        else {
            // If not using screenCapture/capturable or if using screenCapture/capturable and audioDevices is set
            // getMediaDevices.
            newStream = await this.prepareMediaStream(constraints);
        }
        if (kind === "media") {
            newStream.getVideoTracks().forEach((t) => {
                t.addEventListener("ended", this.handleVideoTrackEnded);
            });
        }
        newStream.getAudioTracks().forEach((t) => {
            t.addEventListener("ended", this.handleAudioTrackEnded);
        });
        if (constraints.audio !== false) {
            const newGain = this.noEchoGainAmplifier &&
                typeof constraints.audio !== "boolean" &&
                ((_a = constraints.audio) === null || _a === void 0 ? void 0 : _a.echoCancellation) === false
                ? this.gain * AUDIO_GAIN_MULTIPLIER
                : this.gain;
            newStream = attachGainController(this.logger, newStream, newGain);
        }
        for (const track of (_b = newStream === null || newStream === void 0 ? void 0 : newStream.getTracks()) !== null && _b !== void 0 ? _b : []) {
            if (track.readyState === "ended") {
                this.throwError((0, errors_1.createError)(api_1.ErrorCode.MediaTrackEnded, "track ended unexpectedly", { trackId: track.id, kind: track.kind }));
            }
            if (track.kind === "video") {
                track.enabled = !this.videoPaused;
            }
            if (track.kind === "audio") {
                track.enabled = !this.audioMuted;
            }
        }
        this.logger.trace("prepareStream() finished");
        return newStream;
    }
    async handleAudioTrackEnded() {
        var _a;
        if (((_a = exports.mediaController.micPermission) === null || _a === void 0 ? void 0 : _a.state) === "denied") {
            return;
        }
        await exports.mediaController.enumerateDevices();
        if (!(mediaDevicesList === null || mediaDevicesList === void 0 ? void 0 : mediaDevicesList.some((md) => md.kind === "audioinput" && md.deviceId === this.audioDeviceId))) {
            this.logger.warn("Audio device no longer exists in list of available devices, check to see if device was removed");
            this.emit("audioDeviceRemoved", this.audioDeviceId);
            this.audioDeviceId = null;
        }
    }
    async handleVideoTrackEnded() {
        var _a;
        if (((_a = exports.mediaController.camPermission) === null || _a === void 0 ? void 0 : _a.state) === "denied") {
            return;
        }
        this.logger.warn("video track has ended unexpectedly (perm revoked?)");
        await exports.mediaController.enumerateDevices();
        if (!(mediaDevicesList === null || mediaDevicesList === void 0 ? void 0 : mediaDevicesList.some((md) => md.kind === "videoinput" && md.deviceId === this.videoDeviceId))) {
            this.logger.warn("Video device no longer exists in list of available devices, check to see if device was removed");
            this.emit("videoDeviceRemoved", this.videoDeviceId);
            this.videoDeviceId = null;
        }
    }
    // it is consistent
    async prepareMediaStream(constraints) {
        this.logger.trace("prepareMediaStream()");
        if (!adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE) || adapter_1.device.mediaDevices.getUserMedia == null) {
            this.throwError((0, errors_1.createError)(api_1.ErrorCode.MediaDeviceNotSupported, "MediaDevices not supported", {}));
        }
        this.logger.debug("using constraints", { constraints });
        return adapter_1.device.mediaDevices.getUserMedia(constraints);
    }
    async prepareScreenCaptureStream(constraints) {
        var _a, _b;
        this.logger.trace("prepareScreenCaptureStream()", { constraints });
        let captureStream = null;
        let stream = null;
        if (adapter_1.device.isImplements(adapter_1.Feature.MEDIA_STREAM) &&
            adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE) &&
            typeof adapter_1.device.mediaDevices.getDisplayMedia === "function") {
            const promise = (_b = (_a = adapter_1.device.mediaDevices) === null || _a === void 0 ? void 0 : _a.getDisplayMedia) === null || _b === void 0 ? void 0 : _b.call(_a, { video: constraints.video });
            if (promise != null) {
                try {
                    captureStream = await promise;
                }
                catch (err) {
                    this.videoDeviceChanging = null;
                    throw err;
                }
            }
            if (constraints.audio !== false) {
                stream = await this.prepareMediaStream({ audio: constraints.audio });
            }
            else {
                stream = new adapter_1.device.MediaStream();
            }
        }
        if (captureStream == null || stream == null) {
            throw new Error("unable to capture screen");
        }
        captureStream.getTracks().forEach((t) => {
            tracksMeta.set(t, {
                deviceId: exports.VIDEO_DEVICE_SCREENCAPTURE.deviceId,
            });
            stream === null || stream === void 0 ? void 0 : stream.addTrack(t);
        });
        return stream;
    }
    async prepareCapturableStream(constraints) {
        var _a, _b;
        this.logger.trace("prepareCapturableStream()", { constraints });
        let captureStream = null;
        let stream = null;
        // Grab canvas track
        if (adapter_1.device.isImplements(adapter_1.Feature.MEDIA_STREAM) &&
            ((_a = this.capturable) === null || _a === void 0 ? void 0 : _a.element) != null &&
            adapter_1.device.supportsMediaStreamCapture((_b = this.capturable) === null || _b === void 0 ? void 0 : _b.element)) {
            captureStream = adapter_1.device.captureStream(this.capturable.element, this.capturable.framerate);
        }
        else {
            this.logger.warn("unable to support stream capture");
        }
        // Create MediaStream, adding audioTrack (if there is one)
        if (adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE) && adapter_1.device.isImplements(adapter_1.Feature.MEDIA_STREAM)) {
            stream =
                constraints.audio === false || constraints.audio == null
                    ? new adapter_1.device.MediaStream()
                    : await this.prepareMediaStream({ audio: constraints.audio });
        }
        if (captureStream == null || stream == null) {
            // @todo add log
            throw new Error(`prepareCapturableStream(): unable to generate MediaStream from provided inputs. CapturableStream is null: ${captureStream == null}. Base MediaStream is null: ${stream == null}`);
        }
        captureStream.getTracks().forEach((t) => {
            tracksMeta.set(t, {
                deviceId: exports.VIDEO_DEVICE_CAPTURABLE.deviceId,
            });
            stream === null || stream === void 0 ? void 0 : stream.addTrack(t);
        });
        return stream;
    }
    /**
     * Release audio and video devices
     */
    close(reason = "not provided") {
        var _a;
        try {
            this.stopTracksInternal({ stream: this.source });
            this.audioDeviceChanging = null;
            this.videoDeviceChanging = null;
            this.logger.info("MediaController Class close()", { aggregates: { reason } });
        }
        catch (err) {
            this.emitError((0, errors_1.createError)(api_1.ErrorCode.DisposingError, "unable to dispose media stream controller", {
                className: MediaStreamController.displayName,
                reason,
            }, err));
        }
        // it's ok because audioCtx can be closed already
        (_a = this.audioCtx) === null || _a === void 0 ? void 0 : _a.close().catch(() => {
            // pass
        });
    }
    toJSON() {
        var _a, _b, _c;
        return {
            audioMuted: this.audioMuted,
            videoPaused: this.videoPaused,
            videoDisabled: this.videoDisabled,
            audioDisabled: this.audioDisabled,
            inAudioDeviceTransition: this.inAudioDeviceTransition,
            inVideoDeviceTransition: this.inVideoDeviceTransition,
            hasSync: this.sync != null,
            mscOptions: {
                replaceTracks: (_a = this.options) === null || _a === void 0 ? void 0 : _a.replaceTracks,
                waitingDelay: (_b = this.options) === null || _b === void 0 ? void 0 : _b.waitingDelay,
                defaultLockPolicy: (_c = this.options) === null || _c === void 0 ? void 0 : _c.defaultLockPolicy,
            },
            aggregates: {
                audioDeviceId: this.audioDeviceId,
                videoDeviceId: this.videoDeviceId,
            },
        };
    }
}
exports.MediaStreamController = MediaStreamController;
MediaStreamController.displayName = "MediaStreamController";
//# sourceMappingURL=media-controller.js.map